/**
 * COMO Robot Icon Component
 * 
 * Cute robot icon inspired by friendly AI assistant designs
 * Simple, clean, and professional appearance
 */

import React from 'react';
import { motion } from 'motion/react';

export interface ComoRobotIconProps {
  size?: number;
  isHovered?: boolean;
  isWinking?: boolean;
  mood?: 'idle' | 'happy' | 'thinking' | 'alert' | 'concerned' | 'excited';
}

export function ComoRobotIcon({ size = 80, isHovered = false, isWinking = false, mood = 'idle' }: ComoRobotIconProps) {
  // Color schemes based on mood
  const getColors = () => {
    switch (mood) {
      case 'alert':
      case 'concerned':
        return {
          body: '#93c5fd', // Light blue
          face: '#475569', // Dark slate
          accent: '#ef4444', // Red accent
          glow: 'rgba(239, 68, 68, 0.3)',
        };
      case 'thinking':
        return {
          body: '#c4b5fd', // Light purple
          face: '#475569',
          accent: '#8b5cf6', // Purple accent
          glow: 'rgba(139, 92, 246, 0.3)',
        };
      case 'excited':
      case 'happy':
        return {
          body: '#a7f3d0', // Light green
          face: '#475569',
          accent: '#10b981', // Green accent
          glow: 'rgba(16, 185, 129, 0.3)',
        };
      default: // idle
        return {
          body: '#7dd3fc', // Sky blue
          face: '#475569', // Dark slate
          accent: '#06b6d4', // Cyan accent
          glow: 'rgba(6, 182, 212, 0.3)',
        };
    }
  };

  const colors = getColors();
  const scale = isHovered ? 1.1 : 1;

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      animate={{
        scale: scale,
        y: [0, -3, 0],
      }}
      transition={{
        scale: { duration: 0.3 },
        y: { duration: 3, repeat: Infinity, ease: "easeInOut" }
      }}
    >
      {/* Glow effect */}
      <defs>
        <filter id="comoGlow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        <radialGradient id="bodyGradient">
          <stop offset="0%" stopColor={colors.body} />
          <stop offset="100%" stopColor={colors.accent} stopOpacity="0.7" />
        </radialGradient>
      </defs>

      {/* Outer glow */}
      <motion.circle
        cx="60"
        cy="50"
        r="45"
        fill={colors.glow}
        opacity="0.5"
        animate={{
          scale: isHovered ? [1, 1.1, 1] : [1, 1.05, 1],
          opacity: isHovered ? [0.5, 0.7, 0.5] : [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Antennas — animate <g> transform, not <line> y1/y2 (Motion + SVG line yields undefined attrs). */}
      <g>
        <motion.g
          style={{ transformOrigin: "40px 18px" }}
          animate={mood === "thinking" ? { rotate: [0, -5, 5, 0] } : { rotate: 0 }}
          transition={{
            duration: 1.2,
            repeat: mood === "thinking" ? Infinity : 0,
            ease: "easeInOut",
          }}
        >
          <line
            x1="40"
            y1="18"
            x2="40"
            y2="8"
            stroke={colors.face}
            strokeWidth="4"
            strokeLinecap="round"
          />
          <circle cx="40" cy="8" r="3" fill={colors.accent} />
        </motion.g>

        <motion.g
          style={{ transformOrigin: "80px 18px" }}
          animate={mood === "thinking" ? { rotate: [0, 5, -5, 0] } : { rotate: 0 }}
          transition={{
            duration: 1.2,
            repeat: mood === "thinking" ? Infinity : 0,
            ease: "easeInOut",
            delay: 0.2,
          }}
        >
          <line
            x1="80"
            y1="18"
            x2="80"
            y2="8"
            stroke={colors.face}
            strokeWidth="4"
            strokeLinecap="round"
          />
          <circle cx="80" cy="8" r="3" fill={colors.accent} />
        </motion.g>
      </g>

      {/* Head */}
      <g filter="url(#comoGlow)">
        {/* Head outline */}
        <rect
          x="25"
          y="18"
          width="70"
          height="50"
          rx="12"
          fill={colors.body}
          stroke={colors.face}
          strokeWidth="4"
        />
        
        {/* Screen/Face area */}
        <rect
          x="35"
          y="28"
          width="50"
          height="30"
          rx="8"
          fill={colors.face}
          opacity="0.9"
        />

        {/* Eyes */}
        <motion.g
          animate={{
            scaleY: mood === 'alert' ? [1, 0.2, 1] : 1,
          }}
          transition={{
            duration: 0.15,
            repeat: mood === 'alert' ? Infinity : 0,
            repeatDelay: 2,
          }}
        >
          {/* Left eye */}
          <rect
            x="45"
            y="38"
            width="6"
            height="10"
            rx="3"
            fill="white"
          />
          
          {/* Right eye */}
          <rect
            x="69"
            y="38"
            width="6"
            height="10"
            rx="3"
            fill="white"
          />
          <motion.line
            x1="69"
            y1="43"
            x2="75"
            y2="43"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ opacity: 0 }}
            animate={{ opacity: isWinking ? 1 : 0 }}
            transition={{ duration: 0.12 }}
          />
        </motion.g>

        {/* Smile */}
        <motion.path
          d={
            mood === 'happy' || mood === 'excited'
              ? "M 50 52 Q 60 58 70 52" // Big smile
              : mood === 'concerned'
              ? "M 50 56 Q 60 52 70 56" // Frown
              : "M 50 54 Q 60 57 70 54" // Normal smile
          }
          stroke={colors.accent}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
          animate={{
            d: mood === 'happy' || mood === 'excited'
              ? "M 50 52 Q 60 58 70 52"
              : mood === 'concerned'
              ? "M 50 56 Q 60 52 70 56"
              : "M 50 54 Q 60 57 70 54"
          }}
          transition={{ duration: 0.3 }}
        />
      </g>

      {/* Body */}
      <g>
        {/* Neck */}
        <rect
          x="52"
          y="68"
          width="16"
          height="8"
          rx="2"
          fill={colors.face}
        />

        {/* Body shape */}
        <ellipse
          cx="60"
          cy="95"
          rx="28"
          ry="25"
          fill="url(#bodyGradient)"
          stroke={colors.face}
          strokeWidth="4"
        />

        {/* Chest circle */}
        <circle
          cx="60"
          cy="95"
          r="8"
          fill={colors.face}
          opacity="0.6"
        />
        <motion.circle
          cx="60"
          cy="95"
          r="4"
          fill={colors.accent}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Arms */}
        <motion.g
          animate={{
            rotate: mood === 'excited' ? [0, -10, 10, 0] : 0,
          }}
          transition={{
            duration: 0.5,
            repeat: mood === 'excited' ? Infinity : 0,
          }}
          style={{ transformOrigin: '60px 85px' }}
        >
          {/* Left arm */}
          <path
            d="M 35 82 Q 20 85 18 92 Q 16 98 22 98 L 32 95"
            fill={colors.body}
            stroke={colors.face}
            strokeWidth="3"
            strokeLinejoin="round"
          />
          
          {/* Right arm */}
          <path
            d="M 85 82 Q 100 85 102 92 Q 104 98 98 98 L 88 95"
            fill={colors.body}
            stroke={colors.face}
            strokeWidth="3"
            strokeLinejoin="round"
          />
        </motion.g>
      </g>

      {/* Alert badge (when alert or concerned) */}
      {(mood === 'alert' || mood === 'concerned') && (
        <g>
          <motion.circle
            cx="90"
            cy="25"
            r="10"
            fill="#ef4444"
            animate={{
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
            }}
          />
          <text
            x="90"
            y="30"
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="white"
          >
            !
          </text>
        </g>
      )}

      {/* Thinking dots (when thinking) */}
      {mood === 'thinking' && (
        <g>
          {[0, 1, 2].map((i) => (
            <motion.circle
              key={i}
              cx={50 + i * 10}
              cy="110"
              r="2"
              fill={colors.accent}
              animate={{
                y: [0, -3, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </g>
      )}
    </motion.svg>
  );
}
